import { compatWrapper } from "@backstage/core-compat-api";
import { SignInPage } from "@backstage/core-components";
import {
	discoveryApiRef,
	errorApiRef,
	fetchApiRef,
	identityApiRef,
	microsoftAuthApiRef,
	storageApiRef,
} from "@backstage/core-plugin-api";
import { createApp } from "@backstage/frontend-defaults";
import {
	ApiBlueprint,
	AppRootElementBlueprint,
	createFrontendModule,
	PageBlueprint,
} from "@backstage/frontend-plugin-api";
import apiDocsPlugin from "@backstage/plugin-api-docs/alpha";
import {
	NavContentBlueprint,
	SignInPageBlueprint,
	ThemeBlueprint,
} from "@backstage/plugin-app-react";
import catalogGraphPlugin from "@backstage/plugin-catalog-graph/alpha";
import catalogImportPlugin from "@backstage/plugin-catalog-import/alpha";
import catalogPlugin from "@backstage/plugin-catalog/alpha";
import kubernetesPlugin from "@backstage/plugin-kubernetes/alpha";
import notificationsPlugin from "@backstage/plugin-notifications/alpha";
import orgPlugin from "@backstage/plugin-org/alpha";
import scaffolderPlugin from "@backstage/plugin-scaffolder/alpha";
import searchPlugin from "@backstage/plugin-search/alpha";
import { SignalsDisplay } from "@backstage/plugin-signals";
import techdocsPlugin from "@backstage/plugin-techdocs/alpha";
import { UserSettingsStorage } from "@backstage/plugin-user-settings";
import userSettingsPlugin from "@backstage/plugin-user-settings/alpha";
import { UnifiedThemeProvider } from "@backstage/theme";

import { BundleAdminPage } from "./components/admin/BundleAdminPage";
import { RequirePlatformAdmin, RequireAvdEntitled } from "./components/avd/avdEntitlement";
import { AvdRoutes } from "./components/avd/AvdRoutes";
import { MyAvdPage } from "./components/avd/MyAvdPage";
import { HomePage } from "./components/home/HomePage";
import { AppNav } from "./components/Root";
import { PoweredByTtc } from "./components/Root/PoweredByTtc";
import { TabTitle } from "./components/Root/TabTitle";
import { MyWebAppsPage } from "./components/swa/MyWebAppsPage";
import { RequireSwaEntitled } from "./components/swa/swaEntitlement";
import { WebAppsRoutes } from "./components/swa/WebAppsRoutes";
import { companyDarkTheme, companyLightTheme } from "./theme";

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// NEW FRONTEND SYSTEM. Every plugin is installed from its native `/alpha` entry point; the absence of
// any `convertLegacy*` call is enforced by appContract.test.ts. Traps: docs/app/frontend-system.md.
//
// The one `@backstage/core-compat-api` import is `compatWrapper`, and it is not migration debt: it
// bridges `core-components` and `useApi()` into extensions the new system renders. Still required.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

// THE APP'S OWN EXTENSIONS. `pluginId: "app"` is required, not stylistic — ThemeBlueprint and
// SignInPageBlueprint are both documented as "limited to use by the app plugin".
//
// Don't add `plugins: [catalogPlugin]` here. It is already installed under its real plugin id via
// `features` below, so naming it again throws API_FACTORY_CONFLICT on `plugin.catalog.service`.
const appModule = createFrontendModule({
	pluginId: "app",
	extensions: [
		// THE ONE API EXTENSION — and it is an OVERRIDE, not an addition. @backstage/plugin-app
		// already contributes 23 default APIs (dist/defaultApis.esm.js), storage among them, so look
		// for a default before writing an `ApiBlueprint`. This one replaces the default `storage`
		// factory (plain browser WebStorage) with UserSettingsStorage, so starred entities and
		// theme choice persist in Postgres via @backstage/plugin-user-settings-backend and roam across
		// browsers/devices for the same signed-in user. It overrides rather than adds ONLY because the
		// name matches plugin-app's own extension exactly — `api:app/storage`; any other name would
		// register a second factory for `storageApiRef` and throw API_FACTORY_CONFLICT.
		//
		// NO `signalApi` dep, deliberately. UserSettingsStorage takes an optional `signalApi`
		// to push updates between a colleague's open tabs and devices — but a dep declared HERE is not
		// optional to the resolver, and the ONLY `signalApiRef` factory in Backstage ships in
		// `@backstage/plugin-signals/alpha`, a plugin this app does not install. Declaring it without
		// installing that plugin makes FrontendApiResolver throw "No API factory available for
		// dependency" the first time anything resolves storage — which the catalog's starred-entities
		// API does, on the home page. Auto-discovery does not cover for it either:
		// `discoverAvailableFeatures` is inert unless `app.packages` is set in app-config, and it is
		// not. Settings still roam without it; they just refresh on reload rather than instantly.
		// Adding the dep means adding `signalsPlugin` to `features` in the same change, which also
		// opens a WebSocket to /api/signals — a bigger change than persisting settings needs.
		// appContract.test.ts TRAP 4 pins the pair.
		ApiBlueprint.make({
			name: "storage",
			params: (defineParams) =>
				defineParams({
					api: storageApiRef,
					deps: {
						discoveryApi: discoveryApiRef,
						errorApi: errorApiRef,
						fetchApi: fetchApiRef,
						identityApi: identityApiRef,
					},
					factory: ({ discoveryApi, errorApi, fetchApi, identityApi }) =>
						UserSettingsStorage.create({
							discoveryApi,
							errorApi,
							fetchApi,
							identityApi,
						}),
				}),
		}),

		// AlertDisplay and OAuthRequestDialog ARE app-root defaults — adding ours would render two
		// of each.
		//
		// SignalsDisplay is NOT what keeps signal-driven toasts working: in 1.55 it is a no-op
		// placeholder upstream (plugin.esm.js: `sync: () => null`, commented "to avoid the need for an
		// explicit plugin installation"); the real wiring is `signalsPlugin` in `features`, which this
		// app does not install (see the storage API above). Kept because it is free and upstream may
		// fill it in again.
		AppRootElementBlueprint.make({
			name: "signals-display",
			params: { element: <SignalsDisplay /> },
		}),

		// Fixed bottom-center credit line, mirroring IM GPT's own "Powered by" footer — see
		// PoweredByTtc for why this is a plain anchor+SVG rather than a routed page.
		AppRootElementBlueprint.make({
			name: "powered-by-ttc",
			params: { element: <PoweredByTtc /> },
		}),

		// Pins the browser tab title on every route. An app-root element because that is the only
		// extension point mounted for the whole session; TabTitle.tsx says why no `app.*` config does it.
		AppRootElementBlueprint.make({
			name: "tab-title",
			params: { element: <TabTitle /> },
		}),

		// OUR OWN PAGES. Both render their own <Page>/<Header>, so `noHeader: true` keeps the new
		// system from adding a second one.
		//
		// compatWrapper is required, not decorative: both trees use legacy context (core-components,
		// useApi) and throw outside the bridge. Neither declares a routeRef — everything links to them
		// by string path, so there is no mount point to register.
		PageBlueprint.make({
			name: "home",
			params: {
				path: "/",
				noHeader: true,
				// No HomepageCompositionRoot: it is a ROUTABLE extension of plugin-home, so rendering
				// it here without registering its route ref throws "not discovered in the app element
				// tree". HomePage never used the composition context anyway.
				loader: async () => compatWrapper(<HomePage />),
			},
		}),
		PageBlueprint.make({
			name: "my-avd",
			params: {
				path: "/my-avd",
				noHeader: true,
				// The entitlement gate stays outside MyAvdPage, in the loader. E2E asserts both halves
				// (no sidebar item, request-access card on deep-link) because a gate that breaks open
				// still passes a suite that only checks the happy path.
				// The forms are sub-routes of the page (PageBlueprint mounts `${path}/*`), so the gate
				// covers them too. Template forms in the shell: docs/app/frontend-system.md.
				loader: async () =>
					compatWrapper(
						<RequireAvdEntitled>
							<AvdRoutes page={<MyAvdPage />} />
						</RequireAvdEntitled>,
					),
			},
		}),
		PageBlueprint.make({
			name: "my-web-apps",
			params: {
				path: "/my-web-apps",
				noHeader: true,
				// As my-avd: the gate lives in the loader, not the page.
				loader: async () =>
					compatWrapper(
						<RequireSwaEntitled>
							<WebAppsRoutes page={<MyWebAppsPage />} />
						</RequireSwaEntitled>,
					),
			},
		}),
		PageBlueprint.make({
			name: "admin",
			params: {
				path: "/admin",
				noHeader: true,
				// Same shape as my-avd: the gate lives in the loader, not the page, and E2E asserts
				// both halves. The group is NARROWER than the entitlement one — admins only — so an
				// entitled colleague who is not on the platform team is refused here.
				loader: async () =>
					compatWrapper(
						<RequirePlatformAdmin>
							<BundleAdminPage />
						</RequirePlatformAdmin>,
					),
			},
		}),

		// THE NAV — replaces the default sidebar component; `app/layout` still provides the
		// <SidebarPage> around it. AppNav.tsx says why the items are hard-coded, not auto-discovered.
		NavContentBlueprint.make({
			params: { component: AppNav },
		}),

		// `title` is what the Settings theme picker labels its buttons with, and themes.test.ts
		// asserts on those labels — renaming a theme here is a visible, tested change.
		ThemeBlueprint.make({
			name: "light",
			params: {
				theme: {
					id: "light",
					title: "Light",
					variant: "light",
					Provider: ({ children }) => (
						<UnifiedThemeProvider theme={companyLightTheme}>
							{children}
						</UnifiedThemeProvider>
					),
				},
			},
		}),
		ThemeBlueprint.make({
			name: "dark",
			params: {
				theme: {
					id: "dark",
					title: "Dark",
					variant: "dark",
					Provider: ({ children }) => (
						<UnifiedThemeProvider theme={companyDarkTheme}>
							{children}
						</UnifiedThemeProvider>
					),
				},
			},
		}),

		// SignInPageBlueprint takes a `loader` returning a component, not a component, so it can be
		// code-split.
		SignInPageBlueprint.make({
			params: {
				loader: async () => (props) => (
					<SignInPage
						{...props}
						providers={[
							// Microsoft SSO is production-only, so the button is too: the provider
							// exists only in app-config.production.yaml and local dev is guest-only
							// (ADR-0010), so the button would 404 in dev.
							//
							// Both branches resolve at BUILD time, so each bundle carries exactly one
							// option — which is why E2E runs the dev servers. Enabling real SSO
							// locally needs this condition relaxed too: docs/app/auth.md.
							...(process.env.NODE_ENV === "production"
								? [
										{
											id: "microsoft-auth-provider",
											title: "Microsoft",
											message: "Sign in using Microsoft",
											apiRef: microsoftAuthApiRef,
										},
									]
								: []),
							...(process.env.NODE_ENV !== "production" ? ["guest" as const] : []),
						]}
					/>
				),
			},
		}),
	],
});

const app = createApp({
	features: [
		appModule,
		// Each plugin brings its own pages, APIs, entity content and cards, which is why there is no
		// EntityPage.tsx or SearchPage.tsx to maintain. Inventory: docs/app/frontend-system.md.
		//
		// Upstream constraint: "All routes from a single plugin must be migrated at once" — so a
		// plugin is either fully here or not here.
		apiDocsPlugin,
		catalogPlugin,
		catalogGraphPlugin,
		catalogImportPlugin,
		kubernetesPlugin,
		notificationsPlugin,
		orgPlugin,
		scaffolderPlugin,
		searchPlugin,
		techdocsPlugin,
		userSettingsPlugin,
	],
	// Route bindings live in `app-config.yaml` under `app.routes.bindings` — the new frontend
	// system reads them from config, so there is no `bindRoutes({ bind })` here. Keeping them in config
	// means an operator can re-point an external route without a rebuild. See docs/app/frontend-system.md.
});

// createRoot() takes no element in the new system — the app root is assembled from features.
export default app.createRoot();
